import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, ArrowUpRight, PartyPopper } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { API, EASE } from "@/lib/site";
import { Reveal, Overline } from "./Reveal";
import { OpenAppButton } from "./Buttons";

const KINDS = [["launch", "Notify me at launch"], ["android-tester", "I'll be an Android tester"]];
const GROUP_URL = "https://groups.google.com/g/spotter-app-testers";
const PLAY_URL = "https://play.google.com/apps/testing/com.breezylabs.spotter";

const Step = ({ n, text, href, label, primary, testId }) => (
  <li className="rounded-xl border border-white/[0.08] bg-[#0e0f12]/60 p-4">
    <p className="text-sm leading-relaxed text-[#e4e6ea]"><span className="font-semibold text-[#ff8a3d]">Step {n}</span> - {text}</p>
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-[transform,background-color,border-color] duration-300 hover:-translate-y-0.5 sm:w-auto ${primary ? "bg-[#ff6a3d] text-[#1a0b05] hover:bg-[#ff7b52]" : "border border-[#ff6a3d]/70 text-[#f2f3f5] hover:bg-[#ff6a3d]/10"}`}>
      {label} <ArrowUpRight className="h-4 w-4" />
    </a>
  </li>
);

const TesterSuccess = () => (
  <motion.div data-testid="tester-success-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
    className="w-full max-w-xl rounded-2xl border border-[#ff6a3d]/40 bg-[#16181f]/95 p-5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl sm:p-6">
    <p data-testid="tester-success-title" className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase leading-none sm:text-3xl">
      <PartyPopper className="h-5 w-5 shrink-0 text-[#ff6a3d]" /> You're in - 2 quick steps
    </p>
    <ol className="mt-4 space-y-3">
      <Step n={1} text="Join the tester group (use the Google account on your Android phone):" href={GROUP_URL} label="Join the tester group" primary testId="tester-join-group-btn" />
      <Step n={2} text={<>Then tap &quot;Become a tester&quot; and install Spotter from Google Play:</>} href={PLAY_URL} label="Get Spotter on Android" testId="tester-get-android-btn" />
    </ol>
    <p data-testid="tester-success-note" className="mt-4 text-xs leading-relaxed text-[#b3b8c1]">Step 2 works once Google finishes reviewing the test (a few days). Please keep Spotter installed for 14 days - it's how we get onto the Play Store.</p>
  </motion.div>
);

const testerLine = (n) => {
  if (n === 0) return "Be one of our first 12 Android testers";
  const left = Math.max(0, 12 - n);
  return `Join ${n} other tester${n === 1 ? "" : "s"}${left ? ` · ${left} spot${left === 1 ? "" : "s"} left for the Google Play test` : ""}`;
};

const TesterCount = ({ count, active }) => (
  <motion.p
    data-testid="tester-count-banner"
    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
    className={`flex items-center gap-2 text-sm transition-colors duration-300 ${active ? "text-[#ffd3c2]" : "text-[#c4c8cf]"}`}
  >
    <span className="pulse-dot h-2 w-2 shrink-0 rounded-full bg-[#4ade80]" />
    {testerLine(count)}
  </motion.p>
);

const WaitlistForm = () => {
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState("launch");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [testerDone, setTesterDone] = useState(false);
  const [testers, setTesters] = useState(null);

  useEffect(() => {
    const pick = () => setKind("android-tester");
    window.addEventListener("spotter:pick-tester", pick);
    return () => window.removeEventListener("spotter:pick-tester", pick);
  }, []);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/waitlist/testers/count`)
      .then(({ data }) => { if (live && Number.isFinite(data?.count)) setTesters(data.count); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, source: kind });
      setDone(true);
      setEmail("");
      if (kind === "android-tester") setTesterDone(true);
      else toast.success(data?.already ? "You're already on the list." : "You're in. We'll tell you when Spotter launches.");
    } catch {
      toast.error("Please enter a valid email and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {KINDS.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)} data-testid={`waitlist-kind-${k}`} className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300 ${kind === k ? "border-[#ff6a3d] bg-[#ff6a3d]/20 text-[#f2f3f5]" : "border-white/15 text-[#c4c8cf] hover:border-white/40"}`}>{l}</button>
      ))}
    </div>
    {testers !== null && !testerDone && <TesterCount count={testers} active={kind === "android-tester"} />}
    <form onSubmit={submit} data-testid="waitlist-form" className="flex w-full max-w-xl flex-col gap-3 rounded-full sm:flex-row sm:border sm:border-white/15 sm:bg-[#0e0f12]/60 sm:p-1.5">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="waitlist-email-input" className="h-12 w-full rounded-full border border-white/15 bg-[#0e0f12]/60 px-5 text-base text-[#f2f3f5] placeholder:text-[#8b919b] focus:outline-none focus:ring-2 focus:ring-[#ff6a3d]/60 sm:flex-1 sm:border-0 sm:bg-transparent" />
      <button type="submit" disabled={busy} data-testid="waitlist-submit-btn" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#f2f3f5] px-6 text-sm font-semibold text-[#0e0f12] transition-[background-color,transform] duration-300 hover:bg-white active:scale-95 disabled:opacity-70">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{done ? "Added" : "Join the waitlist"} <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
    {testerDone && <TesterSuccess />}
    </div>
  );
};

export const FinalCta = () => (
  <section id="waitlist" className="px-5 pb-24 sm:px-8 lg:pb-32">
    <div className="grain relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] border border-white/10">
      <img src="/images/rack.jpg" alt="" className="photo-treat absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#ff6a3d]/50 via-[#0e0f12]/85 to-[#0e0f12]" />
      <div className="relative px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
        <Reveal><Overline className="!text-[#ffd3c2]">Coming to Google Play & the App Store</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 font-display text-6xl font-black uppercase leading-[0.86] tracking-tight sm:text-8xl lg:text-[9rem]">Your spot<br />is waiting.</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 max-w-lg text-base text-[#e4e6ea] md:text-lg">Get told the day Spotter launches, or help us get there as an Android tester. You can also start training in the web app today.</p></Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-col gap-5">
          <WaitlistForm />
          <p data-testid="waitlist-privacy-note" className="text-sm leading-relaxed text-[#e4e6ea]">
            <ShieldCheck className="mr-2 inline h-4 w-4 -translate-y-px text-[#ff8a3d]" />
            No ads, no tracking.{" "}
            <Link to="/privacy" data-testid="waitlist-privacy-link" className="underline decoration-[#ff8a3d] underline-offset-4 transition-colors hover:text-[#ff8a3d]">Read our privacy policy</Link>
          </p>
          <div className="flex items-center gap-4 text-sm text-[#c4c8cf]"><span>or</span><OpenAppButton testId="final-cta-open-app" /></div>
        </Reveal>
      </div>
    </div>
  </section>
);
